import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  Mail, 
  Plus, 
  Send, 
  Trash2, 
  Edit, 
  Users, 
  FileText,
  CheckCircle,
  Clock,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon
} from "lucide-react";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExtension from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import {
  getNewsletters,
  createNewsletter,
  updateNewsletter,
  deleteNewsletter,
  sendNewsletter,
  getNewsletterSubscribers,
  deleteNewsletterSubscriber,
} from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";

// Tiptap Editor Toolbar Component
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL invoeren:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-slate-200 bg-slate-100 rounded-t-lg">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`h-8 px-2 ${editor.isActive("bold") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`h-8 px-2 ${editor.isActive("italic") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`h-8 px-2 ${editor.isActive("underline") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <Underline className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={`h-8 px-2 font-bold ${editor.isActive("heading", { level: 2 }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        H2
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={`h-8 px-2 font-bold ${editor.isActive("heading", { level: 3 }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        H3
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`h-8 px-2 ${editor.isActive("bulletList") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`h-8 px-2 ${editor.isActive("orderedList") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: "left" }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <AlignLeft className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: "center" }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <AlignCenter className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        className={`h-8 px-2 ${editor.isActive({ textAlign: "right" }) ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <AlignRight className="h-4 w-4" />
      </Button>
      <div className="w-px h-6 bg-slate-300 mx-1 self-center" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={addLink}
        className={`h-8 px-2 ${editor.isActive("link") ? "bg-slate-300" : "hover:bg-slate-200"}`}
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Newsletter Editor Dialog Component
const NewsletterEditorDialog = ({ 
  open, 
  onOpenChange, 
  editingNewsletter, 
  onSubmit, 
  onReset 
}) => {
  const [subject, setSubject] = useState("");
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExtension,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none min-h-[200px] p-4 focus:outline-none",
      },
    },
  });

  useEffect(() => {
    if (editingNewsletter && editor) {
      setSubject(editingNewsletter.subject);
      editor.commands.setContent(editingNewsletter.content);
    } else if (!editingNewsletter && editor) {
      setSubject("");
      editor.commands.setContent("");
    }
  }, [editingNewsletter, editor, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !editor?.getHTML()) {
      toast.error("Vul alle velden in");
      return;
    }
    await onSubmit({
      subject,
      content: editor.getHTML(),
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    onReset();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingNewsletter ? "Nieuwsbrief Bewerken" : "Nieuwe Nieuwsbrief"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="subject">Onderwerp</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Onderwerp van de nieuwsbrief"
              required
              data-testid="newsletter-subject-input"
            />
          </div>
          <div>
            <Label>Inhoud</Label>
            <div className="mt-2 border border-slate-200 rounded-lg bg-white overflow-hidden">
              <EditorToolbar editor={editor} />
              <div className="min-h-[200px]">
                <EditorContent editor={editor} data-testid="newsletter-content-editor" />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-newsletter-btn"
            >
              {editingNewsletter ? "Bijwerken" : "Opslaan"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminNewsletter() {
  const [newsletters, setNewsletters] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [sending, setSending] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [newsletterRes, subscriberRes] = await Promise.all([
        getNewsletters(),
        getNewsletterSubscribers(),
      ]);
      setNewsletters(newsletterRes.data);
      setSubscribers(subscriberRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fout bij laden van gegevens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (formData) => {
    try {
      if (editingNewsletter) {
        await updateNewsletter(editingNewsletter.id, formData);
        toast.success("Nieuwsbrief bijgewerkt");
      } else {
        await createNewsletter(formData);
        toast.success("Nieuwsbrief aangemaakt");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving newsletter:", error);
      toast.error("Fout bij opslaan");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNewsletter(id);
      toast.success("Nieuwsbrief verwijderd");
      fetchData();
    } catch (error) {
      console.error("Error deleting newsletter:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  const handleSend = async (id) => {
    if (subscribers.filter(s => s.is_active).length === 0) {
      toast.error("Er zijn geen actieve abonnees");
      return;
    }

    setSending(true);
    try {
      const response = await sendNewsletter(id);
      const data = response.data;
      toast.success(
        `Nieuwsbrief verzonden naar ${data.sent_count} abonnees${
          data.failed_count > 0 ? ` (${data.failed_count} mislukt)` : ""
        }`
      );
      fetchData();
    } catch (error) {
      console.error("Error sending newsletter:", error);
      const errorMsg = error.response?.data?.detail || "Fout bij verzenden";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteSubscriber = async (id) => {
    try {
      await deleteNewsletterSubscriber(id);
      toast.success("Abonnee verwijderd");
      fetchData();
    } catch (error) {
      console.error("Error deleting subscriber:", error);
      toast.error("Fout bij verwijderen");
    }
  };

  const resetForm = () => {
    setEditingNewsletter(null);
  };

  const openEditDialog = (newsletter) => {
    setEditingNewsletter(newsletter);
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    if (status === "sent") {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Verzonden
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
        <Clock className="w-3 h-3 mr-1" />
        Concept
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const activeSubscribers = subscribers.filter((s) => s.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Nieuwsbrief</h1>
          <p className="text-slate-500 mt-1">
            Beheer nieuwsbrieven en abonnees
          </p>
        </div>
        <Button
          onClick={openNewDialog}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="create-newsletter-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nieuwe Nieuwsbrief
        </Button>
      </div>

      {/* Newsletter Editor Dialog */}
      <NewsletterEditorDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingNewsletter={editingNewsletter}
        onSubmit={handleSubmit}
        onReset={resetForm}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Totaal Nieuwsbrieven</p>
                <p className="text-2xl font-bold text-slate-900">
                  {newsletters.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Actieve Abonnees</p>
                <p className="text-2xl font-bold text-slate-900">
                  {activeSubscribers}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Send className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Verzonden</p>
                <p className="text-2xl font-bold text-slate-900">
                  {newsletters.filter((n) => n.status === "sent").length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="newsletters" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="newsletters" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" />
            Nieuwsbrieven
          </TabsTrigger>
          <TabsTrigger value="subscribers" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" />
            Abonnees ({subscribers.length})
          </TabsTrigger>
        </TabsList>

        {/* Newsletters Tab */}
        <TabsContent value="newsletters">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Alle Nieuwsbrieven
              </CardTitle>
            </CardHeader>
            <CardContent>
              {newsletters.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    Nog geen nieuwsbrieven aangemaakt
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {newsletters.map((newsletter) => (
                    <div
                      key={newsletter.id}
                      className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors"
                      data-testid={`newsletter-item-${newsletter.id}`}
                    >
                      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-slate-900">
                              {newsletter.subject}
                            </h3>
                            {getStatusBadge(newsletter.status)}
                          </div>
                          <div
                            className="text-sm text-slate-600 line-clamp-2"
                            dangerouslySetInnerHTML={{
                              __html: newsletter.content.replace(/<[^>]*>/g, " ").substring(0, 150) + "...",
                            }}
                          />
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>
                              Aangemaakt:{" "}
                              {formatDate(newsletter.created_at)}
                            </span>
                            {newsletter.sent_at && (
                              <span>
                                Verzonden:{" "}
                                {formatDate(newsletter.sent_at)}
                              </span>
                            )}
                            {newsletter.sent_to_count > 0 && (
                              <span>
                                Naar {newsletter.sent_to_count} abonnees
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {newsletter.status !== "sent" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(newsletter)}
                                data-testid={`edit-newsletter-${newsletter.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    className="bg-blue-600 hover:bg-blue-700"
                                    disabled={sending}
                                    data-testid={`send-newsletter-${newsletter.id}`}
                                  >
                                    <Send className="w-4 h-4 mr-1" />
                                    Verzenden
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      Nieuwsbrief Verzenden
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Weet je zeker dat je deze nieuwsbrief wilt verzenden naar{" "}
                                      <strong>{activeSubscribers}</strong> abonnees? Deze actie kan niet ongedaan worden gemaakt.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleSend(newsletter.id)}
                                      className="bg-blue-600 hover:bg-blue-700"
                                    >
                                      Verzenden
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-newsletter-${newsletter.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Verwijderen</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Weet je zeker dat je deze nieuwsbrief wilt verwijderen?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(newsletter.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Verwijderen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscribers Tab */}
        <TabsContent value="subscribers">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Alle Abonnees
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscribers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Nog geen abonnees</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">
                          Email
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">
                          Naam
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">
                          Status
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-slate-600">
                          Ingeschreven
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-slate-600">
                          Acties
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((subscriber) => (
                        <tr
                          key={subscriber.id}
                          className="border-b border-slate-100 hover:bg-slate-50"
                          data-testid={`subscriber-row-${subscriber.id}`}
                        >
                          <td className="py-3 px-4 text-slate-900">
                            {subscriber.email}
                          </td>
                          <td className="py-3 px-4 text-slate-600">
                            {subscriber.name || "-"}
                          </td>
                          <td className="py-3 px-4">
                            {subscriber.is_active ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200">
                                Actief
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                                Inactief
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-slate-600">
                            {formatDate(subscriber.created_at)}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-subscriber-${subscriber.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Abonnee Verwijderen
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Weet je zeker dat je {subscriber.email} wilt verwijderen?
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Annuleren</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() =>
                                      handleDeleteSubscriber(subscriber.id)
                                    }
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Verwijderen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
